export type AuthRole = 'admin' | 'root' | 'manager' | 'accountant' | 'business' | 'business_user';

export function normalizeAuthRole(role?: string | null): string {
  return (role ?? '').toLowerCase().trim();
}

/**
 * BE-1157: откуда берётся роль текущего пользователя — описано здесь и только здесь.
 * Роль из /auth/me главнее: в токене она с момента входа не меняется. Но пока профиль
 * не загружен, работают уже и гварды, и меню — поэтому фолбэк на токен обязателен.
 * Наружу это отдаёт `AuthService.role()`; напрямую функция нужна только ему и спекам.
 */
export function currentAuthRole(state: {
  currentUser?: { role?: string } | null;
  tokens?: { role?: string } | null;
}): string {
  return normalizeAuthRole(state.currentUser?.role || state.tokens?.role);
}

/**
 * id текущего пользователя — описано здесь и только здесь. /auth/me отдаёт `id`, и с ним
 * же бэк сравнивает, например, `manager_id` в assign_manager; `user_id` — запасной.
 * Наружу это отдаёт `AuthService.currentUserId()`; напрямую функция нужна тем, кто не может
 * внедрить AuthService (диагностика), и спекам.
 */
export function currentUserIdOf(user?: { id?: string; user_id?: string } | null): string | null {
  return user?.id || user?.user_id || null;
}

export function isAdminRole(role?: string | null): boolean {
  const normalizedRole = normalizeAuthRole(role);
  return normalizedRole === 'admin' || normalizedRole === 'root';
}

export function isManagerRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'manager';
}

export function isAdminContourRole(role?: string | null): boolean {
  return isAdminRole(role) || isManagerRole(role);
}

export function isBusinessRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'business';
}

export function isBusinessUserRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'business_user';
}

export function isBusinessContourRole(role?: string | null): boolean {
  return isBusinessRole(role) || isBusinessUserRole(role);
}

export function isAccountantRole(role?: string | null): boolean {
  return normalizeAuthRole(role) === 'accountant';
}

/**
 * BE-824: сотрудник платформы — админ, root, менеджер или бухгалтер. Ровно тот набор,
 * что пускает `require_internal_user()` на бэке; отличается от админского контура тем,
 * что включает бухгалтера, и от бухгалтерского — тем, что включает менеджера.
 */
export function isInternalRole(role?: string | null): boolean {
  return isAdminContourRole(role) || isAccountantRole(role);
}

/** Доступ к разделу бухгалтерии (ADMIN/ROOT или ACCOUNTANT). */
export function isAccountingContourRole(role?: string | null): boolean {
  return isAdminRole(role) || isAccountantRole(role);
}

/** BE-768: русская подпись auth-роли. */
export function authRoleLabel(role: string): string {
  switch (normalizeAuthRole(role)) {
    case 'business':
      return 'Владелец бизнеса';
    case 'business_user':
      return 'Сотрудник бизнеса';
    case 'manager':
      return 'Менеджер';
    case 'accountant':
      return 'Бухгалтер';
    case 'admin':
    case 'root':
      return 'Администратор';
    default:
      return role || '—';
  }
}
